// SWR hook for the experimental-sampling handoff.
//
// Public-side: fetches /api/electionday-state?kind=sampling. Same
// Postgres + edge-cache contract as the editorial hook; the upload /
// extraction / model surface stays admin-only.
//
// Admin-side: pass `admin: true` to read via the authenticated
// endpoint with no caching.

import useSWR, { type SWRConfiguration } from 'swr';
import {
  emptySamplingState,
  type ElectionDaySamplingState,
} from '../data/electionday-sampling-types';

const PUBLIC_URL = '/api/electionday-state?kind=sampling';
const ADMIN_URL = '/api/admin/electionday-sampling';

const fetcher = async (
  url: string,
): Promise<ElectionDaySamplingState> => {
  const res = await fetch(url, {
    cache: 'no-store',
    credentials: 'include',
  });
  if (res.status === 404) return emptySamplingState();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ElectionDaySamplingState;
};

export interface UseSamplingOptions {
  admin?: boolean;
  refreshInterval?: number;
}

export function useElectionDaySampling(
  options: UseSamplingOptions = {},
): {
  data: ElectionDaySamplingState;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
} {
  const url = options.admin ? ADMIN_URL : PUBLIC_URL;
  const config: SWRConfiguration = {
    refreshInterval: options.refreshInterval ?? 12_000,
    revalidateOnFocus: true,
    errorRetryCount: 2,
  };
  const { data, isLoading, error, mutate } =
    useSWR<ElectionDaySamplingState>(url, fetcher, config);
  return {
    data: data ?? emptySamplingState(),
    isLoading,
    error,
    mutate,
  };
}
